import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {useLocation} from "react-router-dom";
import {storage_remove, storage_set} from "../common";
import Log from "../common/Log";
import {useMountEffect} from "../utils/Hooks";
import {DialogContextProvider} from "./DialogContextProvider";
import Header, {HEADER_LOGO_TYPE_BASE, HEADER_TYPE_NONE} from "../views/_common/section/Header";
import Footer, {FOOTER_TYPE_NONE} from "../views/_common/section/Footer";
import {ModelContextProvider} from "./ModelContextProvider";

export const LOCAL_URL_FRONTEND = "http://localhost:9000";
export const LOCAL_URL_BACKEND = "http://localhost:8000";

export const DEVELOPMENT_URL = "http://dev-biz.bear-u.com";
export const DEVELOPMENT_BACK_URL = "https://new-dev.bear-u.com";
export const PRODUCTION_URL = "http://biz.bear-u.com";
export const PRODUCTION_BACK_URL = "https://bear-u.com";

export const BUILD_TYPE_DEVELOPMENT_LOCAL = "development_local";
export const BUILD_TYPE_DEVELOPMENT = "development";
export const BUILD_TYPE_PRODUCTION = "production";

export const getBuildConfig = () => {
    return process.env.REACT_APP_BUILD_CONFIG;
};

export const isDevelopment = () => {
    let build_config = getBuildConfig();
    return build_config === BUILD_TYPE_DEVELOPMENT_LOCAL || build_config === BUILD_TYPE_DEVELOPMENT;
};

export const getImageUrl = (path) => {
    if (!path) return null;

    if (isDevelopment()) {
        return `https://s3.ap-northeast-2.amazonaws.com/dev-img.bear-u.com/b2b_static/${path}`;
    } else {
        return `https://s3.ap-northeast-2.amazonaws.com/img.bear-u.com/b2b_static/${path}`;
    }
};

export const doLogin = (next_page_url = null) => {
    if (next_page_url === null) {
        // 요청 페이지를 기록
        let pathname = window.location.pathname;
        let search = window.location.search;
        next_page_url = pathname + search;

        if (pathname === "/") {
            // pathname 루트인 경우에는 강의목록페이지로 이동 되도록
            next_page_url = "/course-list";
        }
    }

    if (next_page_url) {
        storage_set(
            "next_page_info",
            {
                url: next_page_url,
            },
            1000 * 60 * 5
        ); // 5 min
    } else {
        storage_remove("next_page_info");
    }

    let build_config = getBuildConfig();

    if (build_config === BUILD_TYPE_DEVELOPMENT_LOCAL) {
        window.location.href = LOCAL_URL_FRONTEND;
    } else if (build_config === BUILD_TYPE_DEVELOPMENT) {
        window.location.href = DEVELOPMENT_URL;
    } else if (build_config === BUILD_TYPE_PRODUCTION) {
        window.location.href = PRODUCTION_URL;
    }
};

// broad casts start
export const BROADCAST_SAMPLE = "BROADCAST_SAMPLE"; // value : object
export const BROADCAST_ON_SUBMIT_LOGIN = "BROADCAST_ON_SUBMIT_LOGIN"; // LoginForm의 CustomInput에서 Enter입력(비밀번호 input)
export const BROADCAST_ON_SUBMIT_FIND_EMAIL = "BROADCAST_ON_SUBMIT_FIND_EMAIL"; // FindEmailForm의 CustomInput에서 Enter입력(휴대폰 input)
export const BROADCAST_ON_SUBMIT_FIND_PASSWORD = "BROADCAST_ON_SUBMIT_FIND_PASSWORD"; // FindPasswordForm의 CustomInput에서 Enter입력(이메일 input)
export const BROADCAST_ON_SUBJECT_SELECT = "BROADCAST_ON_SUBJECT_SELECT"; // SubjectList에서 subject 선택 -> SubjectViewer
export const BROADCAST_ON_SUBJECT_END_WITH_AUTO_PLAY = "BROADCAST_ON_SUBJECT_END_WITH_AUTO_PLAY"; // Player에서 한 강의가 끝날시 -> SubjectList (auto_play : true)
export const BROADCAST_ON_NEW_PW_CHANGED = "BROADCAST_ON_NEW_PW_CHANGED"; // CustomPasswordInput에서 새로운 비밀번호 입력 -> 비밀번호 확인 input에서 값을 체크하기 위함
export const BROADCAST_ON_PASS_MOBILE_HEADER_TITLE = "BROADCAST_ON_PASS_MOBILE_HEADER_TITLE"; // 특정 component에서 is_mobile이 true일 시에 Header에서 표시할 타이틀을 보내기 위함
export const BROADCAST_ON_SUBJECT_IS_VIEWED_UPDATE = "BROADCAST_ON_SUBJECT_IS_VIEWED_UPDATE";
export const BROADCAST_ON_FILTER_BUTTON_CHANGE = "BROADCAST_ON_FILTER_BUTTON_CHANGE"; // FilterButtons에서 필터버튼 클릭 -> CourseList에서 현재 필터에 따라 보여줘야할 course_list를 변동함
// broad casts end

const hash = (obj) => {
    if (typeof obj !== "string") {
        return JSON.stringify(obj, (key, value) => {
            if (typeof value === "function") {
                return value.toString();
            } else {
                return value;
            }
        });
    } else {
        return obj;
    }
};

// @TODO
// style: {
//     backgroundColor: "transparent",
// },
export const THEME_TYPE_LIGHT = "light";
export const THEME_TYPE_DARK = "dark";
export const THEME_TYPE_TRANSPARENT = "transparent";
export const THEME_LIST = [THEME_TYPE_LIGHT, THEME_TYPE_DARK, THEME_TYPE_TRANSPARENT];

// fix-> 'root_container'가  @media 768px 일때 min-width: 1240px;
// flexible -> 'root_container' min-width: auto;
export const CONTAINER_TYPE_FIX = "fix";
export const CONTAINER_TYPE_FLEXIBLE = "flexible";
export const CONTAINER_TYPE_FLEXIBLE_TYPE_2 = "flexible type2";

const init_values = {
    theme: THEME_TYPE_LIGHT,
    container: {
        type: CONTAINER_TYPE_FIX,
        style: null,
    },
    header: {
        type: HEADER_TYPE_NONE,
        logo_type: HEADER_LOGO_TYPE_BASE,
        style: null, // apply to root container
        with_spacing: true,
        with_sticky: false,
        menus: null,
        context_menus: null,
        params: null,
    },
    footer: {
        type: FOOTER_TYPE_NONE,
        params: null,
    },
    app_ui: null, // array of components
};

const application_context = createContext();
export const ApplicationContextProvider = ({children}) => {
    const location = useLocation();

    const session_storage = useRef({});
    const broadcasts = useRef([]); // [{key, callback}, ...]

    const theme = useRef(init_values.theme);
    const container = useRef(init_values.container);
    const header = useRef(init_values.header);
    const footer = useRef(init_values.footer);
    const app_ui = useRef(init_values.app_ui);

    const paths = useRef([]);

    const [render_hash, setRenderHash] = useState(null);

    const requestRender = useCallback(() => {
        setRenderHash(
            hash(theme.current) +
                hash(footer.current) +
                hash(container.current) +
                hash(header.current) +
                hash(
                    app_ui.current?.map((elem) => {
                        // elem -> initiated functional component
                        let _hash = "";
                        try {
                            _hash = elem.type?.name + "_" + hash(elem.props);
                        } catch (e) {
                            _hash = elem.type?.name;
                        }
                        return _hash || "";
                    })
                )
        );
    }, []);

    const applyThemeClass = useCallback((new_theme) => {
        // apply classname to body
        THEME_LIST.forEach((_theme) => {
            window.document.body.classList.remove(_theme);
        });
        if (!new_theme) {
            return false;
        } else {
            window.document.body.classList.add(new_theme);
        }
    }, []);

    const setTheme = useCallback(
        (new_theme) => {
            theme.current = new_theme || init_values.theme;
            applyThemeClass(new_theme);
        },
        [applyThemeClass]
    );

    const setContainer = useCallback((new_container) => {
        container.current = new_container || init_values.container;
    }, []);

    const setHeader = useCallback((new_header) => {
        header.current = {
            ...init_values.header,
            // ...header.current,
            ...new_header,
        };
    }, []);

    const setFooter = useCallback((new_footer) => {
        footer.current = {
            ...init_values.footer,
            // ...footer.current,
            ...new_footer,
        };
    }, []);

    const setAppUi = useCallback((new_app_ui) => {
        app_ui.current = new_app_ui || init_values.app_ui;
    }, []);

    useLayoutEffect(() => {
        setTheme(init_values.theme);
        setContainer(init_values.container);
        setHeader(init_values.header);
        setFooter(init_values.footer);
        setAppUi(init_values.app_ui);

        requestRender();
    }, [requestRender, setTheme, setContainer, setHeader, setFooter, setAppUi]);

    useEffect(() => {
        let pathname = location.pathname;
        let search = location.search;
        paths.current.push(pathname + search);
    }, [location.pathname, location.search]);

    const addBroadCast = (keys, callback) => {
        let broadcast = {keys, callback};
        broadcasts.current.push(broadcast);
        return broadcast;
    };

    const removeBroadCast = (broadcast) => {
        broadcasts.current = broadcasts.current.filter((_broadcast) => {
            return _broadcast.callback !== broadcast.callback;
        });
    };

    const _ui = useMemo(() => {
        const _setUi = ({theme, header, container, footer, app_ui}) => {
            setTheme(theme);
            setContainer(container);
            setHeader(header);
            setFooter(footer);
            setAppUi(app_ui);

            requestRender();
        };

        return {
            set: (params) => {
                _setUi({
                    theme: params?.theme || theme.current,
                    container: params?.container || container.current,
                    header: params?.header || header.current,
                    footer: params?.footer || footer.current,
                    app_ui: params?.app_ui || app_ui.current,
                });
            },
            _initialize: (params) => {
                _setUi(params);
            },
        };
    }, [setTheme, setContainer, setHeader, setFooter, setAppUi, requestRender]);

    const _session = useMemo(() => {
        return {
            get: (key) => {
                return session_storage.current[key];
            },
            save: (key, saved_instance) => {
                session_storage.current[key] = saved_instance;
            },
            clear: (key) => {
                session_storage.current[key] = null;
            },
        };
    }, []);

    const _broadcast = useMemo(() => {
        return {
            listen: (keys, callback) => {
                return addBroadCast(keys, callback);
            },
            unlisten: (broadcast) => {
                return removeBroadCast(broadcast);
            },
            send: (key, value) => {
                broadcasts.current.forEach((_broadcast) => {
                    _broadcast.keys.forEach((_key) => {
                        if (_key === key) {
                            _broadcast.callback({key, value});
                        }
                    });
                });
            },
        };
    }, []);

    const actions = {
        ui: _ui,
        session: _session,
        broadcast: _broadcast,
        getPreviousPath: useCallback(() => {
            let previous_path = null;
            try {
                if (paths.current.length > 1) {
                    previous_path = paths.current[paths.current.length - 2];
                }
            } catch (e) {
                Log.error(e);
            }
            return previous_path;
        }, []),
        _requestRender: useCallback(() => {
            requestRender();
        }, [requestRender]),
    };

    return (
        <application_context.Provider value={actions}>
            <ModelContextProvider enable_cache={true}>
                <DialogContextProvider forward_history={false}>
                    <div
                        className={"root-container " + (container.current.type || "")}
                        style={{
                            ...container.current.style,
                            minWidth: "initial",
                        }}>
                        <Header
                            container={container.current}
                            theme={header.current.override_theme || theme.current}
                            header={header.current}
                            hash={render_hash}
                        />
                        {children}
                        <Footer
                            container={container.current}
                            theme={footer.current.override_theme || theme.current}
                            footer={footer.current}
                            hash={render_hash}
                        />
                        {/*@TODO 현수 : app_ui가 불필요하면 제거하기*/}
                        {app_ui.current?.map((elem) => {
                            return <div key={elem.type.name}>{elem}</div>;
                        })}
                    </div>
                </DialogContextProvider>
            </ModelContextProvider>
        </application_context.Provider>
    );
};

// params : {
//     theme: {THEME_TYPE_LIGHT|THEME_TYPE_DARK|THEME_TYPE_TRANSPARENT},
//     header: {
//         type: HEADER_TYPE_{name}, // Constraints of Header.js
//         params: {any},
//     },
//     footer: {
//         type: FOOTER_TYPE_{name}, // Constraints of Footer.js
//         params: {any},
//     },
//     container: {
//         type: CONTAINER_TYPE_{name}, // Constraints of ApplicationContextProvider.js
//         style: {any},
//     },
// }
// @see also ApplicationContextProvider.init_values
// useContext 에서 내려주는 값은 ApplicationContextProvider 의 const actions 변수값 참고
export const useApplicationContext = (init_params) => {
    const c = useContext(application_context);

    useMountEffect(() => {
        if (init_params) {
            c.ui._initialize({
                theme: init_params.theme,
                header: init_params.header,
                container: init_params.container,
                footer: init_params.footer,
                app_ui: init_params.app_ui,
            });
        }
    });

    return c;
};
